import { connect } from 'react-redux';
import { Form, reduxForm } from 'redux-form';
import i18next from 'i18next';
import qs from 'query-string';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';

import { Button } from '@commons/utils/styledLibraryComponents';
import { getTheme } from '@commons/utils/theme';
import { REGEX_MATCH_EMAIL_INSENSITIVE } from '@commons/utils/regex';

import { Input } from '@lib/inpulse/Input';

import { user as userService } from '@services/user';
import ssoService from '@services/sso';

import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@commons/utils/sessionStorage';
import { setLocalStorageItem } from '@commons/utils/localStorage';

import {
  Container,
  Divider,
  FooterContainer,
  Subtitle,
  Title,
  TitleContainer,
} from '../sharedStyledComponents';

import {
  ConnectionContainer,
  ContentContainer,
  EmailContainer,
  ErrorContainer,
  ForgetPasswordContainer,
  ForgetPasswordLink,
  PasswordContainer,
} from './styledComponents';

const ENABLE_SSO_LOGIN_FLOW = true;

// Storage keys used to keep the SSO attempt context across the redirection to the identity provider
const SSO_ATTEMPT_ID_STORAGE_KEY = 'ssoAttemptId';
const SSO_ATTEMPT_EMAIL_STORAGE_KEY = 'ssoAttemptEmail';

const validateEmail = (email) => email && email.match(REGEX_MATCH_EMAIL_INSENSITIVE);

// Not added password validation from userRegistration component
// to avoid oldPassword who not match validation
const validatePassword = (password) => password && password.length >= 6;

const LoginComponent = (props) => {
  const { location } = props;

  const theme = getTheme();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isDisableButton, setIsDisableButton] = useState(true);

  const [isSSOLogin, setIsSSOLogin] = useState(false);

  useLayoutEffect(() => {
    if (!location || !location.search) {
      return;
    }

    const queryParams = qs.parse(location.search);
    if (!queryParams) {
      return;
    }

    const { code, email, sso } = queryParams;

    if (email) {
      setEmail(queryParams.email);
    }

    if (sso === 'true') {
      setIsSSOLogin(true);
    }

    if (code) {
      handleSsoCallback(code);
    }
  }, []);

  useEffect(() => {
    setError(null);

    if (isSSOLogin) {
      setIsDisableButton(!validateEmail(email));
      return;
    }

    setIsDisableButton(!validateEmail(email) || !validatePassword(password));
  }, [email, password, isSSOLogin]);

  const signInByMail = async () => {
    props.pageLoading();

    try {
      await userService.login(email, password);

      props.history.replace('/home');
    } catch (e) {
      setError(i18next.t('AUTHENTICATION.LOGIN.ERROR'));
    } finally {
      props.pageLoaded();
    }
  };

  const signInBySSO = async () => {
    try {
      setError(null);
      setIsDisableButton(true);

      const { redirectUrl, attemptId } = await ssoService.init(email);

      setSessionStorageItem(SSO_ATTEMPT_ID_STORAGE_KEY, attemptId);
      setSessionStorageItem(SSO_ATTEMPT_EMAIL_STORAGE_KEY, email);

      window.location.href = redirectUrl;
    } catch {
      setError(i18next.t('GENERAL.CONNECTION_MICROSOFT_FAILURE'));
    }
  };

  const handleSsoCallback = async (code) => {
    props.pageLoading();
    try {
      const attemptId = getSessionStorageItem(SSO_ATTEMPT_ID_STORAGE_KEY);
      const attemptEmail = getSessionStorageItem(SSO_ATTEMPT_EMAIL_STORAGE_KEY);

      const response = await ssoService.callback(code, attemptId, attemptEmail);
      setLocalStorageItem('loopbackAccessToken', response.token);
      setLocalStorageItem('userId', response.user.id);

      props.history.replace('/home');
    } catch (e) {
      setError(i18next.t('AUTHENTICATION.LOGIN.ERROR'));
    } finally {
      removeSessionStorageItem(SSO_ATTEMPT_ID_STORAGE_KEY);
      removeSessionStorageItem(SSO_ATTEMPT_EMAIL_STORAGE_KEY);

      props.pageLoaded();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSSOLogin) {
      await signInBySSO();

      return;
    }

    await signInByMail();
  };

  return (
    <Container>
      <TitleContainer>
        <Title data-text="Inpulse">
          {i18next.t(!isSSOLogin ? 'AUTHENTICATION.LOGIN.TITLE' : 'AUTHENTICATION.LOGIN.TITLE_SSO')}
          Inpulse
        </Title>
        <Subtitle>{i18next.t('AUTHENTICATION.LOGIN.SUBTITLE')}</Subtitle>
      </TitleContainer>
      <Divider />

      <Form onSubmit={handleSubmit}>
        <ContentContainer isSSO={isSSOLogin}>
          {!!error && <ErrorContainer>{error}</ErrorContainer>}
          <EmailContainer>
            <Input
              label={`* ${i18next.t('AUTHENTICATION.LOGIN.EMAIL')}`}
              type="text"
              value={email}
              width={240}
              autoFocus
              onChange={(event) => setEmail(event.target.value)}
            />
          </EmailContainer>
          {!isSSOLogin && (
            <>
              <PasswordContainer>
                <Input
                  label={`* ${i18next.t('AUTHENTICATION.LOGIN.PASSWORD')}`}
                  type="password"
                  value={password}
                  width={240}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </PasswordContainer>
              <ForgetPasswordContainer>
                <ForgetPasswordLink href="/ask-password-reset" target="_self">
                  {i18next.t('AUTHENTICATION.LOGIN.FORGOT_PASSWORD')}
                </ForgetPasswordLink>
              </ForgetPasswordContainer>
            </>
          )}
          <ConnectionContainer>
            <Button
              buttonCustomStyle={{ width: '240px', justifyContent: 'center' }}
              color="inpulse-default"
              handleClick={handleSubmit}
              icon={'images/inpulse/user-white.svg'}
              isDisabled={isDisableButton}
              label={i18next.t('GENERAL.CONNECTION')}
              type="submit"
            />
          </ConnectionContainer>
        </ContentContainer>
      </Form>
      <Divider />
      <FooterContainer>
        {ENABLE_SSO_LOGIN_FLOW && (
          <Button
            buttonCustomStyle={{
              width: '240px',
              justifyContent: 'center',
              font: theme.fonts.textBigHeight16,
            }}
            color="inpulse-outline"
            handleClick={() => setIsSSOLogin(!isSSOLogin)}
            icon={!isSSOLogin ? 'images/logo-microsoft.svg' : 'images/inpulse/mail-ip-black.svg'}
            label={i18next.t(
              !isSSOLogin ? 'GENERAL.CONNECTION_MICROSOFT' : 'GENERAL.CONNECTION_EMAIL',
            )}
          />
        )}
      </FooterContainer>
    </Container>
  );
};

const mapDispatchToProps = (dispatch) => ({
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default reduxForm({
  form: 'simple',
})(connect(null, mapDispatchToProps)(LoginComponent));
